import React, {
  useState,
  useEffect,
  useContext,
  useRef,
} from 'react';

import {
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  Grid,
  CircularProgress,
  Backdrop,
  TextField,
  Button,
  TableFooter,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import MUIDataTable from 'mui-datatables';

import { FetchContext } from 'src/context/FetchContext';
import { API_ROUTES } from 'src/services/constant';
import Scrollbar from 'src/components/scrollbar';
import { AuthContext } from 'src/context/AuthContext';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { toast } from 'react-hot-toast';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import NewDailyWorkReport from './NewDailyWorkReportForm';
import StaffDailyWorkReportView from './DWRAdminModal';
import { useNavigate } from 'react-router-dom';

dayjs.extend(utc);
dayjs.extend(timezone);

const { PRIVATE_ROUTE } = API_ROUTES;

export default function StaffDailyWorkReportList() {
  // ============================================================
  // MODAL STATE
  // ============================================================

  const [viewOpen, setViewOpen] = useState(false);
  const [selectedDwrId, setSelectedDwrId] = useState(null);

  // ============================================================
  // DATA STATE
  // ============================================================

  const [dwrs, setDwrs] = useState([]);
  const [editableDwrs, setEditableDwrs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [sortEnable, setSortEnable] = useState({});
  const [searchEnable, setSearchEnable] = useState();

  const [selectedstartDate, setselectstartDate] = useState(
    dayjs().subtract(4, 'day').format('YYYY-MM-DD')
  );

  const [selectendDate, setselectendDate] = useState(
    dayjs().format('YYYY-MM-DD')
  );

  const [isValidDate, setIsValidDate] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const { authAxios } = useContext(FetchContext);
  const { state } = useContext(AuthContext);

  const [grandTotaltaskHour, setGrandTotalTaskHour] = useState(0);

  const tableWrapperRef = useRef(null);
  const navigate = useNavigate();
  const [newDwrOpen, setNewDwrOpen] = useState(false);

  const handleOpenNewDwr = () => {
    setNewDwrOpen(true);
  };

  const handleCloseNewDwr = () => {
    setNewDwrOpen(false);
  };

  const handleNewDwrSuccess = () => {
    setNewDwrOpen(false);

    getDwrdata({
      sortField: sortEnable.sortField,
      sortOrder: sortEnable.sortOrder,
      searchQuery: searchEnable,
      page_no: page,
    });
  };


  const handleNewDwr = () => {
    // Replace this with the exact route used by the Manager button.
    navigate('/staff/dwr/new');
  };

  const resetTableHorizontalScroll = () => {
    const wrapper = tableWrapperRef.current;

    if (!wrapper) return;

    const scrollableElements =
      wrapper.querySelectorAll('*');

    scrollableElements.forEach((element) => {
      if (element.scrollWidth > element.clientWidth) {
        element.scrollLeft = 0;
      }
    });

    if (wrapper.scrollWidth > wrapper.clientWidth) {
      wrapper.scrollLeft = 0;
    }
  };

  // ============================================================
  // STATUS HELPERS
  // ============================================================

  const getDwrStatus = (status) => {
    switch (Number(status)) {
      case 1:
        return 'Approved';

      case 2:
        return 'Rejected';

      case 3:
        return 'Open';

      default:
        return 'Pending';
    }
  };

  const getDwrStatusColor = (status) => {
    switch (status) {
      case 'Approved':
        return '#018d35';

      case 'Rejected':
        return '#D32F2F';

      case 'Open':
        return '#1976D2';

      default:
        return '#F59E0B';
    }
  };

  const handleRowDelete = async (event, id) => {
    event.preventDefault();
    event.stopPropagation();

    const confirmed = window.confirm(
      'Are you sure you want to delete this DWR?'
    );

    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);

      await authAxios.post(
        `${PRIVATE_ROUTE?.DELETE_DWR}${id}`
      );

      toast.success('DWR deleted successfully');

      await getDwrdata({
        sortField: sortEnable.sortField,
        sortOrder: sortEnable.sortOrder,
        searchQuery: searchEnable,
        page_no: page,
      });
    } catch (error) {
      console.error('Failed to delete DWR:', error);

      toast.error(
        error?.response?.data?.message ||
        'Failed to delete DWR'
      );

      setLoading(false);
    }
  };

  // ============================================================
  // MODAL OPEN / CLOSE
  // ============================================================

  const handleOpenDwrView = (dwrId) => {
    if (!dwrId) return;

    setSelectedDwrId(dwrId);
    setViewOpen(true);
  };

  const handleCloseDwrView = () => {
    setViewOpen(false);
    setSelectedDwrId(null);
  };

  // ============================================================
  // ROUND
  // ============================================================

  function roundToTwo(num) {
    const value = Number(num);

    if (!Number.isFinite(value)) {
      return 0;
    }

    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  // ============================================================
  // GET DWR DATA
  // ============================================================

  const getDwrdata = (query) => {
    setGrandTotalTaskHour(0);
    setLoading(true);

    let sort_params;

    const page_cout =
      query?.page_no !== undefined &&
        query?.page_no !== null
        ? query.page_no
        : 0;

    const requestedRowsPerPage =
      query?.rowsPerPage || rowsPerPage;

    const start_date =
      selectedstartDate !== null ? selectedstartDate : '';

    const end_date =
      selectendDate !== null ? selectendDate : '';

    const search_query =
      query?.searchQuery ? query.searchQuery : '';

    if (typeof query !== 'string') {
      sort_params =
        `&startDate=${start_date}` +
        `&endDate=${end_date}` +
        `&sortField=${query?.sortField || ''}` +
        `&sortOrder=${query?.sortOrder || ''}` +
        `&search=${search_query}`;
    } else {
      sort_params =
        `&search=${query}` +
        `&startDate=${start_date}` +
        `&endDate=${end_date}`;
    }

    let isMounted = true;

    const getDailyWorkReports = async () => {
      if (!start_date || !end_date) {
        setLoading(false);

        toast.error('Please select date range', {
          duration: 5000,
        });

        return;
      }

      const { data } = await authAxios.get(
        `${PRIVATE_ROUTE?.GET_DWR}?page=${page_cout + 1}` +
        `&per_page=${requestedRowsPerPage}` +
        `${sort_params}`
      );

      if (!isMounted) return;

      const mappedDwrs = (data?.data || []).map((item) => {
        // ========================================================
        // PROJECT MANAGER
        // ========================================================

        const manager_name = (item.project_managers || []).map(
          (project_manager) =>
            `${project_manager.first_name || ''} ${project_manager.last_name || ''}`.trim()
        );

        // ========================================================
        // STATUS
        // ========================================================

        const status = getDwrStatus(item.status);

        // ========================================================
        // CURRENT DWR HOURS
        // ========================================================

        const currentDwrHours = Number(item?.task_hour || 0);

        const roundedCurrentDwrHours = roundToTwo(
          currentDwrHours
        );

        // ========================================================
        // ESTIMATED HOURS
        // ========================================================

        const estimatedHours = Number(
          item?.hoursource?.estimated_hours || 0
        );

        const roundedEstimatedHours = roundToTwo(
          estimatedHours
        );

        // ========================================================
        // EXISTING DWR HOURS
        // ========================================================

        const existingDwrHours = (item?.allDwrForTask || [])
          .filter((dwrItem) => {
            return (
              String(dwrItem?._id) !== String(item?._id) &&
              !dwrItem?.is_deleted &&
              Number(dwrItem?.status) !== 2
            );
          })
          .reduce((total, dwrItem) => {
            return total + Number(dwrItem?.task_hour || 0);
          }, 0);

        const roundedExistingDwrHours = roundToTwo(
          existingDwrHours
        );

        // ========================================================
        // TOTAL SPENT HOURS
        // ========================================================

        const totalSpentHours =
          roundedExistingDwrHours +
          roundedCurrentDwrHours;

        const roundedTotalSpentHours = roundToTwo(
          totalSpentHours
        );

        // ========================================================
        // REMAINING HOURS
        // ========================================================

        const remainingHours = Math.max(
          0,
          roundedEstimatedHours - roundedTotalSpentHours
        );

        const roundedRemainingHours = roundToTwo(
          remainingHours
        );

        // ========================================================
        // MANAGER ESTIMATED HOURS
        // ========================================================
        // This is the persisted estimate_hour value.
        // It represents the amount approved/stamped for this DWR.
        // Pending DWRs show 0.00.
        // ========================================================

        const persistedEstimateHour =
          item?.billing_line_items
            ?.labourCosts?.[0]
            ?.estimate_hour;

        const managerEstimatedHours =
          Number(item?.status) === 1 &&
            persistedEstimateHour !== null &&
            persistedEstimateHour !== undefined &&
            persistedEstimateHour !== ''
            ? roundToTwo(
              Number(persistedEstimateHour)
            )
            : 0;

        // ========================================================
        // ACCURACY
        // ========================================================

        let accuracy = 0;

        if (roundedCurrentDwrHours > 0) {
          accuracy = roundToTwo(
            (roundedCurrentDwrHours /
              Math.max(managerEstimatedHours, 0.01)) *
            100
          );
        }


        // ========================================================
        // RETURN ROW
        // ========================================================

        return {
          _id: item?._id,

          dwr_number:
            item?.dwr_number || '',

          task_number:
            item?.taskdata?.number_str || '',

          job_number:
            item?.job_number || '',

          project_manager:
            manager_name.join(', '),

          accuracy:
            status === 'Approved'
              ? `${accuracy}%`
              : '-',

          task_hour:
            roundedCurrentDwrHours,

          end_date:
            item?.end_date,

          remark:
            item?.remark,

          status,

          user_firstname:
            `${item?.user_firstname || ''} ${item?.user_lastname || ''
              }`.trim(),

          task_date:
            item?.task_date
              ? new Date(item.task_date)
                .toISOString()
                .split('T')[0]
              : '',

          cost_item:
            item?.billing_line_items
              ?.labourCosts?.[0]
              ?.costitem || '',

          taskIsComplate:
            item?.taskdata?.is_completed,

          task_id:
            item?.task_id,

          cost_name:
            item?.billing_line_items
              ?.labourCosts?.[0]
              ?.costitem || '',

          dwrHour:
            roundedCurrentDwrHours,

          pending_dwr_hours:
            roundedRemainingHours,

          managerEstimatedHours,

          estimatedHours:
            roundedEstimatedHours,

          existingDwrHours:
            roundedExistingDwrHours,

          totalSpentHours:
            roundedTotalSpentHours,
        };
      });
      // ========================================================
      // PAGE TOTAL - APPROVED DWR HOURS ONLY
      // ========================================================

      const pageTotalTaskHours = mappedDwrs.reduce(
        (total, row) => {
          if (row.status !== 'Approved') {
            return total;
          }

          return total + Number(row.task_hour || 0);
        },
        0
      );

      setGrandTotalTaskHour(
        roundToTwo(pageTotalTaskHours)
      );

      // ========================================================
      // SHOW PENDING DWRs ON TOP
      // ========================================================

      const sortedDwrs = [...mappedDwrs].sort((a, b) => {
        const aPending = a.status === 'Pending';
        const bPending = b.status === 'Pending';

        if (aPending && !bPending) {
          return -1;
        }

        if (!aPending && bPending) {
          return 1;
        }

        return 0;
      });

      setDwrs(sortedDwrs);
      setEditableDwrs(sortedDwrs);
      setCount(data?.total || 0);
      setLoading(false);

    };

    getDailyWorkReports().catch((err) => {
      if (!isMounted) return;

      console.error(
        'failed to fetch data',
        err
      );

      setLoading(false);

      toast.error(
        'Failed to load Daily Work Reports'
      );
    });

    return () => {
      isMounted = false;
    };
  };

  // ============================================================
  // INITIAL LOAD
  // ============================================================

  useEffect(() => {
    getDwrdata({
      sortField: sortEnable.sortField,
      sortOrder: sortEnable.sortOrder,
      searchQuery: searchEnable,
      page_no: page,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loading) return;

    // MUIDataTable needs time to finish rendering
    // its internal table/scroll containers.
    requestAnimationFrame(() => {
      resetTableHorizontalScroll();

      requestAnimationFrame(() => {
        resetTableHorizontalScroll();
      });
    });

    // Browser scroll restoration can happen after render.
    const timer = setTimeout(() => {
      resetTableHorizontalScroll();
    }, 100);

    return () => clearTimeout(timer);
  }, [editableDwrs, loading]);

  // ============================================================
  // DATE SORT
  // ============================================================

  function handleDateSort() {
    if (
      selectendDate !== null &&
      selectedstartDate !== null
    ) {
      getDwrdata({
        sortField: sortEnable.sortField,
        sortOrder: sortEnable.sortOrder,
        searchQuery: searchEnable,
        page_no: page,
      });
    }
  }

  // ============================================================
  // CLEAR DATE SORT
  // ============================================================

  function handleClearDateSort(
    startDate,
    endDate
  ) {
    if (
      startDate !== null &&
      endDate === null
    ) {
      setIsValid(false);
    } else if (
      startDate === null &&
      endDate !== null
    ) {
      setIsValid(false);
    } else {
      setIsValid(true);
    }

    setselectendDate(null);
    setselectstartDate(null);
  }

  // ============================================================
  // VALID DATE
  // ============================================================

  useEffect(() => {
    if (isValid) {
      getDwrdata({
        sortField: sortEnable.sortField,
        sortOrder: sortEnable.sortOrder,
        searchQuery: searchEnable,
        page_no: page,
      });
    }

    setIsValid(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  // ============================================================
  // KEEP EDITABLE DATA IN SYNC
  // ============================================================

  useEffect(() => {
    setEditableDwrs(dwrs);
  }, [dwrs]);

  // ============================================================
  // TABLE COLUMNS
  // ============================================================

  const columns = [
    {
      name: '_id',
      label: 'Name',
      options: {
        draggable: true,
        sort: false,
        filter: false,
        display: false,
      },
    },

    // ==========================================================
    // DWR NUMBER
    // ==========================================================

    {
      name: 'dwr_number',
      label: 'DWR#',
      options: {
        draggable: true,
        sort: true,
        filter: true,
        display: true,

        customBodyRender: (
          value,
          tableMeta
        ) => {
          const status =
            tableMeta.rowData[9];

          const backgroundColor =
            getDwrStatusColor(status);

          return (
            <span
              role="button"
              tabIndex={0}
              aria-label={`View DWR ${value}`}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();

                handleOpenDwrView(
                  tableMeta.rowData[0]
                );
              }}
              onKeyDown={(event) => {
                if (
                  event.key === 'Enter' ||
                  event.key === ' '
                ) {
                  event.preventDefault();

                  handleOpenDwrView(
                    tableMeta.rowData[0]
                  );
                }
              }}
              style={{
                display: 'inline-block',
                backgroundColor,
                color: '#fff',
                fontWeight: 'bold',
                fontSize: '11px',
                textDecoration: 'none',
                padding: '2px 5px',
                borderRadius: '3px',
                lineHeight: 1.2,
                cursor: 'pointer',
              }}
            >
              {value}
            </span>
          );
        },
      },
    },

    // ==========================================================
    // DATE
    // ==========================================================

    {
      name: 'task_date',
      label: 'Date',
      options: {
        draggable: true,
        sort: false,
        filter: true,
        display: true,
      },
    },

    // ==========================================================
    // TASK
    // ==========================================================

    {
      name: 'task_number',
      label: 'Task#',
      options: {
        draggable: true,
        sort: false,
        filter: true,
        display: true,
      },
    },

    // ==========================================================
    // JOB
    // ==========================================================

    {
      name: 'job_number',
      label: 'Job#',
      options: {
        draggable: false,
        sort: false,
        filter: true,
        display: true,
      },
    },

    // ==========================================================
    // EMPLOYEE
    // ==========================================================

    {
      name: 'user_firstname',
      label: 'Employee Name',
      options: {
        draggable: false,
        sort: true,
        filter: true,
        display: true,
      },
    },

    // ==========================================================
    // PROJECT MANAGER
    // ==========================================================

    {
      name: 'project_manager',
      label: 'Project Manager',
      options: {
        draggable: false,
        sort: false,
        filter: true,
        display: true,
      },
    },

    // ==========================================================
    // COST ITEM
    // ==========================================================

    {
      name: 'cost_item',
      label: 'Cost Item',
      options: {
        draggable: false,
        sort: false,
        filter: true,
        display: true,
      },
    },

    // ==========================================================
    // DWR HOURS
    // ==========================================================

    {
      name: 'task_hour',
      label: 'Dwr Hour',
      options: {
        draggable: false,
        sort: false,
        filter: true,
        display: true,

        customBodyRender: (
          value,
          tableMeta
        ) => {
          const isApproved =
            tableMeta.rowData[9] ===
            'Approved';

          return (
            <span
              style={{
                color: isApproved
                  ? 'green'
                  : '#212B36',
                fontWeight: isApproved
                  ? 'bold'
                  : 'normal',
              }}
            >
              {value}
            </span>
          );
        },
      },
    },

    // ==========================================================
    // STATUS
    // ==========================================================

    {
      name: 'status',
      label: 'Status',
      options: {
        draggable: false,
        sort: true,
        filter: true,
        display: true,
      },
    },

    // ==========================================================
    // ACCURACY
    // ==========================================================

    {
      name: 'accuracy',
      label: 'Accuracy',
      options: {
        draggable: false,
        sort: false,
        filter: true,
        display: true,
      },
    },

    // ==========================================================
    // REMARK
    // ==========================================================

    {
      name: 'remark',
      label: 'Remark',
      options: {
        draggable: false,
        sort: false,
        filter: true,
        display: false,
      },
    },

    // ==========================================================
    // TASK STATUS
    // ==========================================================

    {
      name: 'taskIsComplate',
      label: 'Task Status',
      options: {
        draggable: false,
        sort: false,
        filter: true,
        display: false,
      },
    },

    // ==========================================================
    // TASK ID
    // ==========================================================

    {
      name: 'task_id',
      label: 'Task id',
      options: {
        draggable: false,
        sort: false,
        filter: true,
        display: false,
      },
    },

    // ==========================================================
    // COST NAME
    // ==========================================================

    {
      name: 'cost_name',
      label: 'cost name',
      options: {
        draggable: false,
        sort: false,
        filter: true,
        display: false,
      },
    },

    // ==========================================================
    // DWR HOUR INTERNAL
    // ==========================================================

    {
      name: 'dwrHour',
      label: 'dwrHour',
      options: {
        draggable: false,
        sort: false,
        filter: true,
        display: false,
      },
    },

    // ==========================================================
    // PENDING DWR HOURS
    // ==========================================================

    {
      name: 'pending_dwr_hours',
      label: 'Pending DWR Hours',
      options: {
        draggable: false,
        sort: true,
        filter: true,
        display: false,

        customBodyRender: (
          value
        ) => (
          <span>
            {value}
          </span>
        ),
      },
    },

    // ==========================================================
    // MANAGER ESTIMATED HOURS
    // ==========================================================

    {
      name: 'managerEstimatedHours',
      label: 'Manager Estimated Hours',
      options: {
        draggable: false,
        sort: false,
        filter: false,
        display: true,

        customBodyRender: (
          value,
          tableMeta
        ) => {
          const rowIndex =
            tableMeta.rowIndex;

          const rowData =
            tableMeta.rowData;

          const status =
            rowData[9];

          const isApproved =
            status === 'Approved';

          return (
            <TextField
              variant="outlined"
              size="small"
              type="number"
              disabled={isApproved}
              InputProps={{
                readOnly: isApproved,
              }}
              inputProps={{
                min: 0,
                step: '0.01',

                onKeyDown: (
                  event
                ) => {
                  if (
                    [
                      'e',
                      'E',
                      '+',
                      '-',
                    ].includes(
                      event.key
                    )
                  ) {
                    event.preventDefault();
                  }
                },
              }}
              value={
                editableDwrs[
                  rowIndex
                ]?.managerEstimatedHours ??
                '0'
              }
              onChange={(event) => {
                if (isApproved) {
                  return;
                }

                let newValue =
                  event.target.value;

                // Allow empty value while editing.
                if (
                  newValue === ''
                ) {
                  setEditableDwrs(
                    (prev) => {
                      const updated = [
                        ...prev,
                      ];

                      updated[
                        rowIndex
                      ] = {
                        ...updated[
                        rowIndex
                        ],

                        managerEstimatedHours:
                          '',
                      };

                      return updated;
                    }
                  );

                  return;
                }

                // Allow:
                // 1
                // 1.
                // 1.2
                // 12
                // 12.50
                if (
                  !/^\d*\.?\d*$/.test(
                    newValue
                  )
                ) {
                  return;
                }

                const numericValue =
                  parseFloat(
                    newValue
                  );

                if (
                  !Number.isNaN(
                    numericValue
                  ) &&
                  numericValue < 0
                ) {
                  newValue = '0';
                }

                setEditableDwrs(
                  (prev) => {
                    const updated = [
                      ...prev,
                    ];

                    updated[
                      rowIndex
                    ] = {
                      ...updated[
                      rowIndex
                      ],

                      managerEstimatedHours:
                        newValue,
                    };

                    return updated;
                  }
                );
              }}
              sx={{
                width: '80px',

                pointerEvents:
                  isApproved
                    ? 'none'
                    : 'auto',

                backgroundColor:
                  isApproved
                    ? '#f5f5f5'
                    : 'white',
              }}
            />
          );
        },
      },
    },

    // ==========================================================
    // ACTION
    // ==========================================================

    {
      name: 'Action',
      label: 'Action',
      options: {
        sort: false,
        filter: false,
        display: true,
        customBodyRender: (
          value,
          tableMeta
        ) => {
          const dwrId = tableMeta.rowData[0];
          const status = tableMeta.rowData[9];

          const isPending =
            status === 'Pending';

          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {/* EDIT */}
              <EditIcon
                color="inherit"
                titleAccess="Edit DWR"
                sx={{
                  cursor: 'pointer',
                  fontSize: 22,
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();

                  handleOpenDwrView(dwrId);
                }}
              />

              {/* DELETE */}
              <DeleteIcon
                color={
                  isPending
                    ? 'inherit'
                    : 'disabled'
                }
                titleAccess={
                  isPending
                    ? 'Delete DWR'
                    : 'Only Pending DWRs can be deleted'
                }
                sx={{
                  cursor: isPending
                    ? 'pointer'
                    : 'default',
                  fontSize: 22,
                }}
                onClick={
                  isPending
                    ? (event) =>
                      handleRowDelete(
                        event,
                        dwrId
                      )
                    : undefined
                }
              />
            </div>
          );
        },
      },
    },
  ];

  // ============================================================
  // TABLE OPTIONS
  // ============================================================

  const options = {
    tableId: 'DWR_List_View',
    rowHover: false,
    jumpToPage: true,
    responsive: 'standard',
    print: true,
    fixedHeader: true,
    searchOpen: true,
    filter: false,
    count,
    selectableRows: 'multiple',
    pagination: true,

    downloadOptions: {
      filterOptions: {
        useDisplayedColumnsOnly: true,
      },
    },

    // ==========================================================
    // DOWNLOAD
    // ==========================================================

    onDownload: (
      buildHead,
      buildBody,
      columns,
      data
    ) => {
      const formattedData =
        data.map((item) => {
          if (
            typeof item === 'string'
          ) {
            return item;
          }

          if (
            typeof item === 'object' &&
            Array.isArray(
              item.data
            )
          ) {
            const result = [];

            item.data.forEach(
              (el) => {
                if (
                  typeof el !==
                  'object'
                ) {
                  result.push(el);
                }

                if (
                  typeof el ===
                  'object'
                ) {
                  if (
                    el.props?.to
                  ) {
                    result.push(
                      el.props
                        ?.children
                        ?.props
                        ?.children
                    );
                  }

                  if (
                    Array.isArray(
                      el.props
                        ?.children
                    )
                  ) {
                    const childrenArray =
                      el.props
                        .children;

                    const combinedString =
                      `${childrenArray[1]}, ` +
                      `${childrenArray[5]}, ` +
                      `${childrenArray[9]}, ` +
                      `${childrenArray[13]}`;

                    result.push(
                      combinedString
                    );
                  }
                }
              }
            );

            return result;
          }

          return null;
        });

      const result =
        formattedData.map(
          (
            rowData,
            index
          ) => ({
            index,
            data: rowData,
          })
        );

      const footerData = [
        '',
        '',
        '',
        '',
        '',
        '',
        'Total Task Hours',
        `${roundToTwo(
          grandTotaltaskHour
        )}`,
        '',
      ];

      return (
        `${buildHead(columns)}` +
        `${buildBody(result)}\n` +
        `${footerData.join(',')}\n`
      );
    },

    // ==========================================================
    // ROW PROPS
    // ==========================================================

    setRowProps: (
      row,
      dataIndex,
      rowIndex
    ) => {
      const status = row[9];

      const isApproved =
        status === 'Approved';

      return {
        style: {
          backgroundColor:
            isApproved
              ? 'rgba(0, 128, 0, 0.08)'
              : rowIndex % 2 === 0
                ? 'rgba(101, 200, 208, 0.15)'
                : 'white',

          color:
            isApproved
              ? 'green'
              : '#212B36',

          fontWeight:
            isApproved
              ? 'bold'
              : 'normal',

          cursor:
            isApproved
              ? 'not-allowed'
              : 'pointer',

          opacity:
            isApproved
              ? 0.6
              : 1,
        },

        onClick:
          isApproved
            ? (event) =>
              event.stopPropagation()
            : undefined,
      };
    },

    // ==========================================================
    // ROW SELECTABLE
    // ==========================================================

    isRowSelectable: (
      dataIndex
    ) => {
      const rowData =
        dwrs?.[dataIndex];

      const status =
        rowData?.status;

      return status !== 'Approved';
    },

    // ==========================================================
    // TABLE PROPS
    // ==========================================================

    setTableProps: () => {
      return {
        border:
          '1px solid rgba(101, 200, 208,0.15)',
        size: 'small',
      };
    },

    // ==========================================================
    // SEARCH
    // ==========================================================

    onSearchChange: (
      searchQuery
    ) => {
      setSearchEnable(
        searchQuery
      );

      if (
        searchQuery !== null &&
        searchQuery.length > 2
      ) {
        if (
          Object.keys(
            sortEnable
          ).length !== 0
        ) {
          getDwrdata({
            sortField:
              sortEnable.sortField,

            sortOrder:
              sortEnable.sortOrder,

            searchQuery,
          });
        } else {
          getDwrdata({
            searchQuery,
          });
        }
      }

      if (
        searchQuery === null
      ) {
        if (
          Object.keys(
            sortEnable
          ).length !== 0
        ) {
          getDwrdata({
            sortField:
              sortEnable.sortField,

            sortOrder:
              sortEnable.sortOrder,
          });
        } else {
          getDwrdata('');
        }
      }
    },

    // ==========================================================
    // ROWS PER PAGE
    // ==========================================================

    rowsPerPage,
    page,
    serverSide: true,

    onTableChange: (
      action,
      tableState
    ) => {
      const {
        name,
        direction,
      } = tableState.sortOrder || {};

      const sortField = name;
      const sortOrder = direction;
      const searchQuery = searchEnable;

      // Change page size
      if (action === 'changeRowsPerPage') {
        const newRowsPerPage =
          tableState.rowsPerPage;

        setRowsPerPage(newRowsPerPage);
        setPage(0);

        getDwrdata({
          sortField,
          sortOrder,
          searchQuery,
          page_no: 0,
          rowsPerPage: newRowsPerPage,
        });

        return;
      }

      // Sort
      if (action === 'sort') {
        setSortEnable({
          sortField,
          sortOrder,
        });

        setPage(tableState.page);

        getDwrdata({
          sortField,
          sortOrder,
          searchQuery,
          page_no: tableState.page,
        });

        return;
      }

      // Change page
      if (action === 'changePage') {
        setPage(tableState.page);

        getDwrdata({
          sortField,
          sortOrder,
          searchQuery,
          page_no: tableState.page,
        });
      }
    },
    // ==========================================================
    // TABLE FOOTER
    // ==========================================================

    customTableBodyFooterRender:
      (opts) => {
        return (
          <TableFooter>
            <TableRow
              sx={{
                '&:last-child td, &:last-child th':
                {
                  border: 0,
                },

                border:
                  '1px solid #808080',
              }}
            >
              {opts.columns.map(
                (
                  col,
                  index
                ) => {
                  if (
                    col.name ===
                    'dwr_number'
                  ) {
                    return (
                      <TableCell
                        key={index}
                        sx={{
                          color:
                            'black',

                          fontWeight:
                            'bold',

                          fontSize:
                            '18px',

                          border:
                            '1px solid #808080 !important',
                        }}
                      />
                    );
                  }

                  if (
                    col.name ===
                    'task_date'
                  ) {
                    return (
                      <TableCell
                        key={index}
                        sx={{
                          border:
                            '1px solid #808080 !important',
                        }}
                      />
                    );
                  }

                  if (
                    col.name ===
                    'task_number'
                  ) {
                    return (
                      <TableCell
                        key={index}
                        sx={{
                          border:
                            '1px solid #808080 !important',
                        }}
                      />
                    );
                  }

                  if (
                    col.name ===
                    'job_number'
                  ) {
                    return (
                      <TableCell
                        key={index}
                        sx={{
                          border:
                            '1px solid #808080 !important',
                        }}
                      />
                    );
                  }

                  if (
                    col.name ===
                    'user_firstname'
                  ) {
                    return (
                      <TableCell
                        key={index}
                        sx={{
                          border:
                            '1px solid #808080 !important',
                        }}
                      />
                    );
                  }

                  if (
                    col.name ===
                    'project_manager'
                  ) {
                    return (
                      <TableCell
                        key={index}
                        sx={{
                          border:
                            '1px solid #808080 !important',
                        }}
                      />
                    );
                  }

                  if (
                    col.name ===
                    'cost_item'
                  ) {
                    return (
                      <TableCell
                        key={index}
                        sx={{
                          border:
                            '1px solid #808080 !important',
                        }}
                      />
                    );
                  }

                  if (
                    col.name ===
                    'task_hour'
                  ) {
                    return (
                      <TableCell
                        key={index}
                        sx={{
                          color:
                            'black',

                          fontWeight:
                            'bold',

                          fontSize:
                            '18px',

                          border:
                            '1px solid #808080 !important',

                          textAlign:
                            'end',
                        }}
                      >
                        Total:
                      </TableCell>
                    );
                  }

                  if (
                    col.name ===
                    'status'
                  ) {
                    return (
                      <TableCell
                        key={index}
                        sx={{
                          color:
                            'black',

                          fontWeight:
                            'bold',

                          fontSize:
                            '18px',

                          border:
                            '1px solid #808080 !important',
                        }}
                      >
                        {roundToTwo(
                          grandTotaltaskHour
                        )}
                      </TableCell>
                    );
                  }

                  if (
                    col.name ===
                    'accuracy'
                  ) {
                    return (
                      <TableCell
                        key={index}
                        sx={{
                          border:
                            '1px solid #808080 !important',
                        }}
                      />
                    );
                  }

                  if (
                    col.name ===
                    'Action'
                  ) {
                    return (
                      <TableCell
                        key={index}
                        sx={{
                          border:
                            '1px solid #808080 !important',
                        }}
                      />
                    );
                  }

                  return null;
                }
              )}
            </TableRow>
          </TableFooter>
        );
      },

    // ==========================================================
    // BULK APPROVE TOOLBAR
    // ==========================================================

    customToolbarSelect: (
      selectedRows,
      displayData,
      setSelectedRows
    ) => (
      <div
        style={{
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          padding: '5px',
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={(event) => {
            const proceed =
              window.confirm(
                '⚠️ Are you sure you want to approve the selected DWR(s)?'
              );

            if (proceed) {
              handleBulkAction(
                event,
                'Approved',
                selectedRows,
                displayData,
                setSelectedRows
              );
            }
          }}
        >
          Bulk Approve
        </Button>
      </div>
    ),
  };

  // ============================================================
  // BULK ACTION
  // ============================================================

  const handleBulkAction = async (
    event,
    status,
    selectedRows,
    displayData,
    setSelectedRows
  ) => {
    event.preventDefault();

    if (
      !selectedRows?.data?.length
    ) {
      return;
    }

    setLoading(true);

    const selectedData =
      selectedRows.data.map(
        (row) => {
          const rowIndex =
            row.dataIndex;

          const rowData =
            displayData[
              rowIndex
            ]?.data;

          return {
            dwrId:
              rowData?.[0],

            managerEstimatedHours:
              editableDwrs[
                rowIndex
              ]?.managerEstimatedHours ||
              0,
          };
        }
      );

    try {
      const response =
        await authAxios.post(
          PRIVATE_ROUTE.EDIT_BLUK_DWR_STATUS,
          {
            dwrs:
              selectedData,
          }
        );

      if (
        response.status === 200
      ) {
        toast.success(
          response.data?.message ||
          'Updated successfully!'
        );
      } else {
        toast.error(
          'Update failed!'
        );
      }

      await getDwrdata({
        page_no: 0,
      });

      setSelectedRows([]);
    } catch (error) {
      console.error(
        'Bulk update error:',
        error
      );

      toast.error(
        'Something went wrong!'
      );
    }

    setLoading(false);
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <>
      <Grid
        sx={{
          p: {
            xs: 2,
            sm: 3,
            md: 4,
          },

          pb: 0,
        }}
      >
        <Stack
          direction={{
            xs: 'column',
            sm: 'row',
          }}
          alignItems={{
            xs: 'stretch',
            sm: 'center',
          }}
          justifyContent="space-between"
          spacing={2}
          mb={2}
        >
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              mb: 0,
            }}
          >
            Staff Daily Work Report
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenNewDwr}
          >
            New Daily Work Report
          </Button>
        </Stack>

        {/* ========================================================
            DATE FILTER
        ======================================================== */}

        <Grid
          container
          spacing={2}
          sx={{
            py: 2,
            alignItems: 'center',
            width: '100%',
            m: 0,
          }}
        >
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
          >
            <LocalizationProvider
              dateAdapter={
                AdapterDayjs
              }
            >
              <DatePicker
                label="Start Date"
                inputFormat="YYYY-MM-DD"
                value={
                  selectedstartDate
                }
                onChange={(
                  value
                ) => {
                  const formattedDate =
                    value
                      ? dayjs(
                        value
                      ).format(
                        'YYYY-MM-DD'
                      )
                      : null;

                  setselectstartDate(
                    formattedDate
                  );
                }}
                renderInput={(
                  props
                ) => (
                  <TextField
                    {...props}
                    fullWidth
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={3}
          >
            <LocalizationProvider
              dateAdapter={
                AdapterDayjs
              }
            >
              <DatePicker
                label="End Date"
                inputFormat="YYYY-MM-DD"
                value={
                  selectendDate
                }
                onChange={(
                  value
                ) => {
                  const formattedDate =
                    value
                      ? dayjs(
                        value
                      ).format(
                        'YYYY-MM-DD'
                      )
                      : null;

                  setselectendDate(
                    formattedDate
                  );
                }}
                renderInput={(
                  props
                ) => (
                  <TextField
                    {...props}
                    fullWidth
                  />
                )}
                minDate={
                  selectedstartDate
                }
                onError={(
                  newError
                ) =>
                  setIsValidDate(
                    newError
                  )
                }
              />
            </LocalizationProvider>
          </Grid>

          <Grid
            item
            xs={12}
            sm="auto"
          >
            <Button
              size="large"
              type="submit"
              variant="contained"
              onClick={
                handleDateSort
              }
              disabled={
                selectendDate ===
                null ||
                selectedstartDate ===
                null
              }
              sx={{
                width: {
                  xs: '100%',
                  sm: 'auto',
                },
              }}
            >
              Submit
            </Button>
          </Grid>
        </Grid>

        {/* ========================================================
            TABLE
        ======================================================== */}

        <Grid
          ref={tableWrapperRef}
          sx={{
            width: '100%',
            overflow: 'hidden',
          }}
        >
          <Scrollbar>
            <TableContainer
              sx={{
                width: '100%',
                // overflowX: 'auto',
                overflow: 'visible',
                WebkitOverflowScrolling:
                  'touch',
              }}
            >
              <Table>
                {loading && (
                  <Backdrop
                    sx={{
                      color: '#fff',
                      zIndex: (
                        theme
                      ) =>
                        theme.zIndex
                          .drawer + 1,
                    }}
                    open={loading}
                  >
                    <CircularProgress color="inherit" />
                  </Backdrop>
                )}

                <MUIDataTable
                  options={
                    options
                  }
                  columns={
                    columns
                  }
                  data={
                    editableDwrs
                  }
                />

                {!dwrs?.length &&
                  !loading && (
                    <TableBody>
                      <TableRow>
                        <TableCell
                          align="center"
                          colSpan={
                            6
                          }
                          sx={{
                            py: 3,
                          }}
                        >
                          No Data Found
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
              </Table>
            </TableContainer>
          </Scrollbar>
        </Grid>
      </Grid>

      {/* ========================================================
          STAFF DWR VIEW MODAL
      ======================================================== */}

      <StaffDailyWorkReportView
        open={
          viewOpen
        }
        dwrId={
          selectedDwrId
        }
        onClose={
          handleCloseDwrView
        }
        onUpdated={() => {
          handleCloseDwrView();

          getDwrdata({
            sortField:
              sortEnable.sortField,

            sortOrder:
              sortEnable.sortOrder,

            searchQuery:
              searchEnable,

            page_no: page,
          });
        }}
      />
      <NewDailyWorkReport
        open={newDwrOpen}
        onClose={handleCloseNewDwr}
        onSuccess={handleNewDwrSuccess}
      />
    </>
  );
}